"use client";

import { useState, type ReactNode } from "react";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { industries } from "@/data/marketing/industries";
import { attributedHref, startHref, trackMarketing } from "./launch";
import s from "./Marketing.module.scss";

export function MarketingLink({
    href,
    children,
    className,
    event = "cta_start_click",
    source,
    ...props
}: {
    href: string;
    children: ReactNode;
    className?: string;
    event?: string;
    source: string;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">) {
    return (
        <a
            {...props}
            href={href}
            className={className}
            onClick={(e) => {
                e.currentTarget.href = attributedHref(href);
                trackMarketing(event, { source, destination: href.split("?")[0] });
            }}
        >
            {children}
        </a>
    );
}

export function Logo() {
    return (
        <a className={s.logo} href="/" aria-label="TEZAM — на главную">
            <img className={s.brandMark} src="/images/tezam-logo.png" width="130" height="29" alt="TEZAM" />
        </a>
    );
}

export function MarketingChrome({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={s.root} data-marketing-native>
            <a href="#main-content" className={s.skip}>
                К содержимому
            </a>
            <header className={s.header}>
                <div className={s.headerInner}>
                    <Logo />
                    <nav
                        aria-label="Основная навигация"
                        className={`${s.nav} ${open ? s.navOpen : ""}`}
                        id="marketing-navigation"
                    >
                        {["Возможности", "Для бизнеса", "Как начать", "Тарифы", "Наш канал"].map((label, i) => (
                            <a
                                key={label}
                                onClick={() => setOpen(false)}
                                href={`/#${["product", "industries", "how", "pricing", "channel"][i]}`}
                            >
                                {label}
                            </a>
                        ))}
                    </nav>
                    <div className={s.headerActions}>
                        <a className={s.login} href="/login">
                            Войти
                        </a>
                        <MarketingLink href={startHref()} source="header" className={s.buttonSmall}>
                            Начать <ArrowUpRight size={16} />
                        </MarketingLink>
                        <button
                            className={s.menuButton}
                            aria-label={open ? "Закрыть меню" : "Открыть меню"}
                            aria-expanded={open}
                            aria-controls="marketing-navigation"
                            onClick={() => setOpen(!open)}
                        >
                            {open ? <X /> : <Menu />}
                        </button>
                    </div>
                </div>
            </header>
            <main id="main-content">{children}</main>
            <footer className={s.footer}>
                <div className={s.footerTop}>
                    <div>
                        <Logo />
                        <p>
                            Ваш бизнес. Ваши клиенты.
                            <br />
                            Одна система для работы.
                        </p>
                        <a href="mailto:team@tezam.by">team@tezam.by</a>
                        <br />
                        <a href="tel:+375339188819">+375 33 918-88-19</a>
                    </div>
                    <div>
                        <h3>Продукт</h3>
                        <a href="/#product">Возможности</a>
                        <a href="/tariffs">Все тарифы</a>
                        <a href="/portfolio">Проекты TEZAM</a>
                        <a href="/#channel">Канал TEZAM</a>
                        <a href="/stati">Статьи и новости</a>
                        <a href={startHref()}>Создать свой сервис</a>
                    </div>
                    <div>
                        <h3>Для бизнеса</h3>
                        {industries.slice(0, 4).map((x) => (
                            <a href={`/solutions/${x.id}`} key={x.id}>
                                {x.label}
                            </a>
                        ))}
                    </div>
                    <div>
                        <h3>Ещё решения</h3>
                        {industries.slice(4).map((x) => (
                            <a href={`/solutions/${x.id}`} key={x.id}>
                                {x.label}
                            </a>
                        ))}
                    </div>
                </div>
                <div className={s.footerBottom}>
                    <span>
                        © 2026 ООО «Тезамбай» · УНП 193899918
                        <br />
                        Минск, ул. Некрасова, 114, пом. 84
                    </span>
                    <div>
                        <a href="/politika-konfidentsialnosti">Конфиденциальность</a>
                        <a href="/terms">Условия использования</a>
                        <a href="/oplata-i-poluchenie-uslugi">Оплата</a>
                    </div>
                </div>
            </footer>
            <CookieConsentBanner />
        </div>
    );
}
